import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { MONATE_DE, fmtPeriod, joinPeriod, splitPeriod } from "@/lib/period";
import { PayrollAdjustDialog } from "@/components/PayrollAdjustDialog";
import { PayrollCorrectionDialog } from "@/components/PayrollCorrectionDialog";
import { UnpaidAbsenceWarning } from "@/components/UnpaidAbsenceWarning";
import { ExpenseClaimsCard } from "@/components/ExpenseClaimsCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageCircle } from "lucide-react";
import { BonusCasesCard } from "@/components/BonusCasesCard";
import { toast } from "sonner";
import { WorktimeSummariesCard } from "@/components/WorktimeSummariesCard";
import { BonusSummariesCard } from "@/components/BonusSummariesCard";

export function Payroll() {
  const [runs, setRuns] = useState([]);
  const [elmLoading, setElmLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [form, setForm] = useState({
    period: "",
    employee_id: "",
    payment_date: "",
    hours_worked: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailRun, setDetailRun] = useState(null);
  const [adjustRun, setAdjustRun] = useState(null);
  const [correctRun, setCorrectRun] = useState(null);

  const [postingId, setPostingId] = useState(null);
  const [error, setError] = useState("");
  const [hoursSuggestion, setHoursSuggestion] = useState(null);

  const [painOpen, setPainOpen] = useState(false);
  const [painPeriod, setPainPeriod] = useState("");
  const [painPreview, setPainPreview] = useState(null);
  const [painExecDate, setPainExecDate] = useState("");
  const [painLoading, setPainLoading] = useState(false);
  const [painError, setPainError] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  const loadRuns = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const [runsRes, empRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/payroll-runs`),
        apiClient.get(`/companies/${companyId}/employees`, { params: { include_archived: true } }),
      ]);
      setRuns(runsRes.data || []);
      setAllEmployees(empRes.data || []);
      setEmployees((empRes.data || []).filter((e) => !e.archived_at));
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Lohnläufe.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Iter258: Ist-Stunden aus der Zeiterfassung als Vorschlag für Stundenlöhner
  useEffect(() => {
    const sel = employees.find((e) => e._id === form.employee_id);
    if (!companyId || sel?.employment_type !== "hourly" || !/^\d{4}-\d{2}$/.test(form.period)) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get(`/companies/${companyId}/work-time/payroll-hours`, {
          params: { employee_id: form.employee_id, month: form.period },
        });
        if (!cancelled && r.data.hours > 0) {
          setForm((prev) => (prev.hours_worked ? prev : { ...prev, hours_worked: String(r.data.hours) }));
          setHoursSuggestion(r.data);
        } else if (!cancelled) {
          setHoursSuggestion(null);
        }
      } catch { /* Zeiterfassung optional */ }
    })();
    return () => { cancelled = true; };
  }, [companyId, form.employee_id, form.period, employees]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.period || !form.employee_id || !form.payment_date) {
      setError("Bitte Periode, Mitarbeitende und Zahlungsdatum angeben.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        period: form.period,
        employee_id: form.employee_id,
        payment_date: form.payment_date,
      };
      // Iter111b: Stundenlohn-Inputs nur senden, wenn Employee hourly ist
      const selectedEmp = employees.find((e) => e._id === form.employee_id);
      if (selectedEmp?.employment_type === "hourly") {
        if (!form.hours_worked || Number(form.hours_worked) <= 0) {
          setError("Stundenlöhner: bitte geleistete Stunden angeben.");
          setSaving(false);
          return;
        }
        payload.hours_worked = Number(form.hours_worked);
      }
      await apiClient.post(`/companies/${companyId}/payroll-runs`, payload);
      setForm({
        period: "",
        employee_id: "",
        payment_date: "",
        hours_worked: "",
      });
      await loadRuns();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Erfassen des Lohnlaufs.");
    } finally {
      setSaving(false);
    }
  };

  const handlePay = async (runId) => {
    setPostingId(runId);
    try {
      const r = await apiClient.post(`/companies/${companyId}/payroll-runs/pay`, { run_ids: [runId] });
      toast.success(`Nettolohn ${Number(r.data.amount).toFixed(2)} CHF ausbezahlt (Verbindlichkeit Nettolöhne an Bank)`);
      await loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zahlung fehlgeschlagen");
    } finally {
      setPostingId(null);
    }
  };

  const handlePost = async (runId) => {
    setPostingId(runId);
    setError("");
    try {
      await apiClient.post(`/payroll-runs/${runId}/post`);
      await loadRuns();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Buchen des Lohnlaufs.");
    } finally {
      setPostingId(null);
    }
  };

  const periods = [...new Set(runs.map((r) => r.period))].sort().reverse();
  const empName = (id) => {
    const e = allEmployees.find((x) => x._id === id);
    return e ? `${e.first_name} ${e.last_name}${e.archived_at ? " (archiviert)" : ""}` : "—";
  };
  const filteredRuns = form.employee_id ? runs.filter((r) => r.employee_id === form.employee_id) : runs;

  const loadPainPreview = async (period) => {
    setPainPeriod(period);
    setPainPreview(null);
    setPainError("");
    if (!period) return;
    setPainLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/payroll-pain001/preview`, {
        params: { period },
      });
      setPainPreview(res.data);
      setPainExecDate(res.data.default_execution_date || "");
    } catch (e) {
      setPainError(e.response?.data?.detail || "Fehler beim Laden der Vorschau.");
    } finally {
      setPainLoading(false);
    }
  };

  const openPainDialog = () => {
    setPainOpen(true);
    setPainError("");
    if (periods.length > 0) loadPainPreview(periods[0]);
  };

  const downloadPain001 = async () => {
    setPainLoading(true);
    setPainError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/payroll-pain001`, {
        params: { period: painPeriod, execution_date: painExecDate },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pain001_lohn_${painPeriod}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = "Fehler beim Erzeugen der Zahlungsdatei.";
      if (e.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await e.response.data.text()).detail || detail;
        } catch { /* keep default */ }
      }
      setPainError(detail);
    } finally {
      setPainLoading(false);
    }
  };

  const downloadElm = async () => {
    const years = [...new Set(runs.map((r) => String(r.period || "").slice(0, 4)).filter(Boolean))].sort().reverse();
    const year = years[0] || String(new Date().getFullYear());
    setElmLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/payroll/elm-export`, {
        params: { year },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `elm_salary_declaration_${year}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ELM-Deklaration ${year} heruntergeladen — zur manuellen Einreichung (Swissdec-Vorbereitung)`);
    } catch (e) {
      let detail = "ELM-Export fehlgeschlagen.";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* keep */ }
      }
      toast.error(detail);
    } finally {
      setElmLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Lohnläufe</h1>
        <p className="mt-1 text-sm text-slate-500">
          Erfassen Sie monatliche Lohnläufe. Buchungen werden erst mit Klick auf &quot;Buchen&quot; ins Journal übernommen.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-3xl border border-slate-200 bg-white shadow-sm" data-testid="payroll-form-card">
        <form className="space-y-3" onSubmit={handleCreate} data-testid="payroll-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="payroll-period-month">Monat</Label>
              <div className="flex gap-1">
                <select id="payroll-period-month" className="border border-slate-200 rounded-md px-2 py-1 text-sm flex-1"
                  value={splitPeriod(form.period).month}
                  onChange={(e) => handleChange("period", joinPeriod(splitPeriod(form.period).year || String(new Date().getFullYear()), e.target.value))}
                  data-testid="payroll-period-month-select">
                  <option value="">Monat …</option>
                  {MONATE_DE.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
                </select>
                <select className="border border-slate-200 rounded-md px-2 py-1 text-sm w-24"
                  value={splitPeriod(form.period).year || String(new Date().getFullYear())}
                  onChange={(e) => handleChange("period", joinPeriod(e.target.value, splitPeriod(form.period).month || String(new Date().getMonth() + 1).padStart(2, "0")))}
                  data-testid="payroll-period-year-select">
                  {[-1, 0, 1].map((d) => { const y = String(new Date().getFullYear() + d); return <option key={y} value={y}>{y}</option>; })}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-employee">Mitarbeitende</Label>
              <select
                id="payroll-employee"
                className="border border-slate-200 rounded-md px-2 py-1 text-sm w-full"
                value={form.employee_id}
                onChange={(e) => handleChange("employee_id", e.target.value)}
                data-testid="payroll-employee-select"
              >
                <option value="">Mitarbeitende wählen</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.first_name} {emp.last_name}{emp.employment_type === "hourly" ? " (Std)" : ""}
                  </option>
                ))}
              </select>
              <UnpaidAbsenceWarning companyId={companyId} employeeId={form.employee_id} period={form.period} onApproved={loadRuns} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-payment-date">Zahlungsdatum</Label>
              <SwissDateField
                id="payroll-payment-date"
                
                value={form.payment_date}
                onChange={(e) => handleChange("payment_date", e.target.value)}
                data-testid="payroll-payment-date-input"
              />
            </div>
            {/* Iter111b: Stunden-Input nur bei Stundenlöhner */}
            {(() => {
              const sel = employees.find((e) => e._id === form.employee_id);
              if (sel?.employment_type !== "hourly") return null;
              return (
                <div className="space-y-1">
                  <Label htmlFor="payroll-hours-worked">
                    Stunden {sel.hourly_rate ? `(à CHF ${sel.hourly_rate.toFixed(2)})` : ""}
                  </Label>
                  <Input
                    id="payroll-hours-worked"
                    type="number"
                    step="0.25"
                    min="0"
                    placeholder="z.B. 168.5"
                    value={form.hours_worked}
                    onChange={(e) => handleChange("hours_worked", e.target.value)}
                    data-testid="payroll-hours-worked-input"
                  />
                  {hoursSuggestion && (
                    <p className="text-[11px] text-emerald-700" data-testid="payroll-hours-suggestion">
                      Vorschlag aus Zeiterfassung: {hoursSuggestion.hours} Std ({hoursSuggestion.entry_count} Einträge in {hoursSuggestion.month})
                    </p>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="payroll-save-button"
            >
              {saving ? "Speichern ..." : "Lohnlauf erfassen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="payroll-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Lohnläufe</h2>
          <div className="flex items-center gap-2">
            {loading && (
              <span className="text-xs text-slate-400" data-testid="payroll-loading">
                Lade ...
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadElm}
              disabled={runs.length === 0 || elmLoading}
              title="ELM-5.0-orientierte Jahres-Lohndeklaration als XML (Swissdec-Vorbereitung, manuelle Einreichung)"
              data-testid="payroll-elm-export-button"
            >
              {elmLoading ? "Erzeuge…" : "ELM-XML (Swissdec)"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openPainDialog}
              disabled={runs.length === 0}
              data-testid="payroll-pain001-open-button"
            >
              Zahlungsdatei (pain.001)
            </Button>
          </div>
        </div>
        {form.employee_id && (
          <div className="mb-3 flex items-center gap-2 text-sm" data-testid="payroll-filter-banner">
            <Badge className="bg-sky-100 text-sky-800">Gefiltert: {empName(form.employee_id)}</Badge>
            <button
              type="button"
              className="text-sky-700 underline hover:text-sky-900"
              onClick={() => handleChange("employee_id", "")}
              data-testid="payroll-filter-clear-button"
            >
              alle anzeigen
            </button>
          </div>
        )}
        <Table data-testid="payroll-table">
          <TableHeader>
            <TableRow>
              <TableHead>Periode</TableHead>
              <TableHead>Mitarbeiter</TableHead>
              <TableHead>Zahlungsdatum</TableHead>
              <TableHead>Bruttolohn</TableHead>
              <TableHead>AN-Abzüge</TableHead>
              <TableHead>Netto</TableHead>
              <TableHead>AG-Kosten</TableHead>
              <TableHead>Gesamtkosten</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Details</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRuns.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center text-sm text-slate-400"
                  data-testid="payroll-empty-state"
                >
                  {form.employee_id ? "Keine Lohnläufe für diesen Mitarbeiter." : "Noch keine Lohnläufe erfasst."}
                </TableCell>
              </TableRow>
            )}
            {filteredRuns.map((run) => (
              <TableRow key={run._id} data-testid={`payroll-row-${run._id}`}>
                <TableCell>
                  <span data-testid={`payroll-run-period-${run._id}`}>{fmtPeriod(run.period)}</span>
                  {run.adjustments && (
                    <Badge className="ml-1.5 bg-amber-100 text-amber-800" title={`Manuell angepasst${run.adjustments.note ? ": " + run.adjustments.note : ""}`} data-testid={`payroll-adjusted-badge-${run._id}`}>
                      angepasst
                    </Badge>
                  )}
                  {run.run_type === "supplement" && (
                    <Badge className="ml-1.5 bg-violet-100 text-violet-800" title="Nachtrags-Lohnlauf — z.B. GF-Bonus, der nach der Buchung des Hauptlaufs eingetroffen ist" data-testid={`payroll-supplement-badge-${run._id}`}>
                      Nachtrag
                    </Badge>
                  )}
                  {run.run_type === "correction" && (
                    <Badge className="ml-1.5 bg-rose-100 text-rose-800" title={`Korrekturlauf zu Lohnabrechnung ${fmtPeriod(run.correction?.original_period || run.period)}${run.correction?.reason ? " – " + run.correction.reason : ""} · weist nur die Differenz aus`} data-testid={`payroll-correction-badge-${run._id}`}>
                      Korrektur{run.net_salary < 0 ? " · Rückforderung" : ""}
                    </Badge>
                  )}
                  {run.corrected_by_run_ids?.length > 0 && (
                    <Badge className="ml-1.5 bg-slate-200 text-slate-700" title={`Korrigiert durch ${run.corrected_by_run_ids.length} Korrekturlauf/-läufe`} data-testid={`payroll-corrected-badge-${run._id}`}>
                      korrigiert
                    </Badge>
                  )}
                  {run.breakdown?.unpaid_absence?.amount > 0 && (
                    <Badge className="ml-1.5 bg-orange-100 text-orange-800" title={`Unbezahlter Urlaub: ${run.breakdown.unpaid_absence.days} Tage → Kürzung CHF ${Number(run.breakdown.unpaid_absence.amount).toFixed(2)}`} data-testid={`payroll-unpaid-badge-${run._id}`}>
                      unbez. Urlaub {run.breakdown.unpaid_absence.days} T
                    </Badge>
                  )}
                  {run.warnings?.some((w) => w.code === "unpaid_absence_pending") && !run.posted && (
                    <Badge className="ml-1.5 bg-amber-100 text-amber-900" title={run.warnings.find((w) => w.code === "unpaid_absence_pending").message} data-testid={`payroll-warning-badge-${run._id}`}>
                      ⚠ unbez. Urlaub offen
                    </Badge>
                  )}
                  {run.breakdown?.receivable_offset?.amount > 0 && (
                    <Badge className="ml-1.5 bg-teal-100 text-teal-800" title={`Rückforderung aus Lohnkorrektur ${(run.breakdown.receivable_offset.periods || []).map(fmtPeriod).join(", ")} verrechnet: CHF ${Number(run.breakdown.receivable_offset.amount).toFixed(2)}${run.breakdown.receivable_offset.remaining_open > 0 ? ` · noch offen CHF ${Number(run.breakdown.receivable_offset.remaining_open).toFixed(2)}` : ""}`} data-testid={`payroll-offset-badge-${run._id}`}>
                      Rückford. verrechnet {Number(run.breakdown.receivable_offset.amount).toFixed(2)}
                    </Badge>
                  )}
                  {run.run_type === "correction" && run.settlement === "receivable" && (
                    run.settled_at ? (
                      <Badge className="ml-1.5 bg-emerald-100 text-emerald-800" title={`Rückforderung vollständig verrechnet im Lohnlauf ${fmtPeriod(run.settled_in_period)}`} data-testid={`payroll-settled-badge-${run._id}`}>
                        verrechnet in {fmtPeriod(run.settled_in_period)}
                      </Badge>
                    ) : (
                      <Badge className="ml-1.5 bg-amber-100 text-amber-900" title="Rückforderung wird mit dem nächsten regulären Lohnlauf verrechnet" data-testid={`payroll-open-receivable-badge-${run._id}`}>
                        offen CHF {(Math.abs(run.net_salary) - (run.settlement_applied || 0)).toFixed(2)}
                      </Badge>
                    )
                  )}
                  {run.breakdown?.expense_reimbursement?.amount > 0 && (
                    <Badge className="ml-1.5 bg-cyan-100 text-cyan-800" title={`Spesenrückerstattung AHV-frei: ${run.breakdown.expense_reimbursement.count} Belege, CHF ${Number(run.breakdown.expense_reimbursement.amount).toFixed(2)} (2060 an 2050)`} data-testid={`payroll-expense-badge-${run._id}`}>
                      Spesen {Number(run.breakdown.expense_reimbursement.amount).toFixed(2)}
                    </Badge>
                  )}
                  {run.breakdown?.historic_values?.base_salary && (
                    <Badge className="ml-1.5 bg-sky-100 text-sky-800" title={`Historischer Fixlohn CHF ${Number(run.breakdown.historic_values.base_salary.value).toFixed(2)} (gültig am Monatsende) – aktueller Lohn CHF ${Number(run.breakdown.historic_values.base_salary.current).toFixed(2)} ab ${formatDate(run.breakdown.historic_values.base_salary.current_valid_from)}`} data-testid={`payroll-historic-badge-${run._id}`}>
                      hist. Lohn
                    </Badge>
                  )}
                </TableCell>
                <TableCell data-testid={`payroll-run-employee-${run._id}`}>{empName(run.employee_id)}</TableCell>
                <TableCell>{formatDate(run.payment_date)}</TableCell>
                <TableCell>{run.gross_salary.toFixed(2)}</TableCell>
                <TableCell>{(run.social_security + run.pension + run.tax_withholding).toFixed(2)}</TableCell>
                <TableCell>{run.net_salary.toFixed(2)}</TableCell>
                <TableCell>{(run.employer_social_cost + run.employer_pension_cost).toFixed(2)}</TableCell>
                <TableCell>{run.total_company_cost.toFixed(2)}</TableCell>
                <TableCell>
                  {run.posted ? "Gebucht" : "Offen"}
                  {run.posted && run.payslip_delivery && (
                    <span className="ml-1.5 text-[11px] font-mono" data-testid={`payroll-delivery-${run._id}`}
                      title={`E-Mail: ${run.payslip_delivery.email?.sent ? "gesendet an " + run.payslip_delivery.email.to : (run.payslip_delivery.email?.error || "nicht gesendet")} · WhatsApp: ${run.payslip_delivery.whatsapp?.sent ? "gesendet an " + run.payslip_delivery.whatsapp.to : (run.payslip_delivery.whatsapp?.error || "nicht gesendet")}`}>
                      <Mail className={`inline h-3.5 w-3.5 ${run.payslip_delivery.email?.sent ? "text-emerald-700" : "text-slate-300"}`} />
                      <MessageCircle className={`inline h-3.5 w-3.5 ml-0.5 ${run.payslip_delivery.whatsapp?.sent ? "text-emerald-700" : "text-slate-300"}`} />
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/payslip/${run._id}`, '_blank')}
                    data-testid={`payroll-payslip-button-${run._id}`}
                  >
                    Lohnabrechnung
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailRun(run)}
                        data-testid={`payroll-detail-button-${run._id}`}
                      >
                        Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Lohnlauf {fmtPeriod(run.period)}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Bruttolohn</span>
                          <span className="font-mono">{run.gross_salary.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>AN Sozialversicherungen</span>
                          <span className="font-mono">{run.social_security.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>AN BVG</span>
                          <span className="font-mono">{run.pension.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Quellensteuer</span>
                          <span className="font-mono">{run.tax_withholding.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-dashed border-slate-200 pt-2 mt-2">
                          <span>Netto</span>
                          <span className="font-mono">{run.net_salary.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between pt-2">
                          <span>AG Sozialversicherungen</span>
                          <span className="font-mono">{run.employer_social_cost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>AG BVG</span>
                          <span className="font-mono">{run.employer_pension_cost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-dashed border-slate-200 pt-2 mt-2">
                          <span>Gesamtkosten Firma</span>
                          <span className="font-mono">{run.total_company_cost.toFixed(2)}</span>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {!run.posted && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mr-1 border-amber-300 text-amber-800"
                      onClick={() => setAdjustRun(run)}
                      title="Lohn für diesen Monat anpassen: Bonus, Spesen/Zulage, Abzug, Stunden/Ansatz oder Grundlohn"
                      data-testid={`payroll-adjust-button-${run._id}`}
                    >
                      Anpassen
                    </Button>
                  )}
                  {!run.posted && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handlePost(run._id)}
                      disabled={postingId === run._id}
                      data-testid={`payroll-post-button-${run._id}`}
                    >
                      {postingId === run._id ? "Bucht ..." : "Buchen"}
                    </Button>
                  )}
                  {run.posted && !run.paid_at && (
                    <Button type="button" size="sm" className="bg-emerald-700 text-white hover:bg-emerald-800"
                      onClick={() => handlePay(run._id)} disabled={postingId === run._id}
                      title="Nettolohn auszahlen: Verbindlichkeit Nettolöhne an Bank" data-testid={`payroll-pay-button-${run._id}`}>
                      {postingId === run._id ? "Zahlt ..." : "Zahlung buchen"}
                    </Button>
                  )}
                  {run.posted && run.paid_at && (
                    <span className="text-xs text-emerald-700" data-testid={`payroll-paid-${run._id}`}>
                      {run.settlement === "receivable" ? "Rückforderung gebucht (1190)" : `bezahlt ${formatDate(run.paid_on)}`}
                    </span>
                  )}
                  {run.posted && run.run_type !== "correction" && (
                    <Button type="button" variant="outline" size="sm" className="ml-1 border-rose-300 text-rose-800"
                      onClick={() => setCorrectRun(run)}
                      title="Gebuchten Lohnlauf korrigieren: Korrekturlauf mit Differenzbuchung (Original bleibt unverändert)"
                      data-testid={`payroll-correct-button-${run._id}`}>
                      Korrigieren
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <PayrollAdjustDialog run={adjustRun} companyId={companyId} employee={employees.find((e) => e._id === adjustRun?.employee_id)} onClose={() => setAdjustRun(null)} onSaved={loadRuns} />
      <PayrollCorrectionDialog run={correctRun} companyId={companyId} employee={allEmployees.find((e) => e._id === correctRun?.employee_id)} onClose={() => setCorrectRun(null)} onSaved={loadRuns} />
      <WorktimeSummariesCard companyId={companyId} />
      <BonusCasesCard companyId={companyId} onChanged={loadRuns} highlightId={new URLSearchParams(window.location.search).get("bonus")} />
      <BonusSummariesCard companyId={companyId} onChanged={loadRuns} />
      <ExpenseClaimsCard companyId={companyId} onPaid={loadRuns} />

      <Dialog open={painOpen} onOpenChange={setPainOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-pain001-dialog">
          <DialogHeader>
            <DialogTitle>Lohnzahlungsdatei (pain.001)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="pain-period">Periode</Label>
                <select
                  id="pain-period"
                  className="border border-slate-200 rounded-md px-2 py-1.5 text-sm w-full"
                  value={painPeriod}
                  onChange={(e) => loadPainPreview(e.target.value)}
                  data-testid="payroll-pain001-period-select"
                >
                  {periods.map((p) => (
                    <option key={p} value={p}>{fmtPeriod(p)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pain-exec-date">Ausführungsdatum</Label>
                <SwissDateField
                  id="pain-exec-date"
                  
                  value={painExecDate}
                  onChange={(e) => setPainExecDate(e.target.value)}
                  data-testid="payroll-pain001-exec-date-input"
                />
              </div>
            </div>

            {painError && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="payroll-pain001-error">
                {painError}
              </div>
            )}
            {painLoading && !painPreview && <p className="text-xs text-slate-400">Lade Vorschau ...</p>}

            {painPreview && (
              <>
                {painPreview.company_iban_missing && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="payroll-pain001-company-iban-warning">
                    Firmen-IBAN fehlt. Bitte in den Firmeneinstellungen die IBAN des Belastungskontos hinterlegen.
                  </div>
                )}
                <div className="rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="payroll-pain001-payable-list">
                  {painPreview.payable.map((p) => (
                    <div key={p.run_id} className="flex justify-between px-3 py-1.5">
                      <div>
                        <span>{p.employee_name}</span>
                        <span className="ml-2 text-xs text-slate-400 font-mono">{p.iban}</span>
                      </div>
                      <span className="font-mono">{p.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {painPreview.payable.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">Keine zahlbaren Lohnläufe in dieser Periode.</div>
                  )}
                  <div className="flex justify-between px-3 py-1.5 font-medium bg-slate-50">
                    <span>Total ({painPreview.payable.length} Zahlungen)</span>
                    <span className="font-mono" data-testid="payroll-pain001-total">CHF {painPreview.total_amount.toFixed(2)}</span>
                  </div>
                </div>
                {painPreview.skipped.length > 0 && (
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="payroll-pain001-skipped-list">
                    <p className="font-medium mb-1">Export blockiert – IBAN fehlt oder IBAN-Änderung noch nicht freigegeben (Mitarbeitende):</p>
                    {painPreview.skipped.map((s) => (
                      <p key={s.run_id}>• {s.employee_name}: {s.reasons.join(", ")}</p>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 text-xs"
                      disabled={painLoading}
                      onClick={async () => {
                        setPainLoading(true);
                        try {
                          const r = await apiClient.post(
                            `/companies/${companyId}/payroll-pain001/send-iban-reminders`,
                            null,
                            { params: { period: painPeriod } }
                          );
                          const failed = (r.data.results || []).filter((x) => !x.sent);
                          if (r.data.sent_count > 0) {
                            toast.success(`Onboarding-Link an ${r.data.sent_count} Mitarbeiter gesendet`);
                          }
                          failed.forEach((f) => toast.error(`${f.employee_name}: ${f.reason}`));
                          if (!r.data.results?.length) toast.info("Keine Mitarbeiter mit fehlender IBAN gefunden");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
                        } finally {
                          setPainLoading(false);
                        }
                      }}
                      data-testid="payroll-pain001-send-reminders-button"
                    >
                      Onboarding-Link per E-Mail senden
                    </Button>
                  </div>
                )}
                {painPreview.excluded?.length > 0 && (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="payroll-pain001-excluded-list">
                    <p className="font-medium mb-1">Ohne Zahlung (Nettolohn 0 — blockiert nicht):</p>
                    {painPreview.excluded.map((s) => (
                      <p key={s.run_id}>• {s.employee_name}</p>
                    ))}
                  </div>
                )}
                {painPreview.company_iban && (
                  <p className="text-xs text-slate-500">
                    Belastungskonto: <span className="font-mono">{painPreview.company_iban}</span>
                  </p>
                )}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-full bg-slate-900 text-slate-50 px-4"
                    onClick={downloadPain001}
                    disabled={painLoading || painPreview.payable.length === 0 || painPreview.skipped.length > 0 || painPreview.company_iban_missing || !painExecDate}
                    data-testid="payroll-pain001-download-button"
                  >
                    {painLoading ? "Erzeuge ..." : "pain.001 herunterladen"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
